import { Button, ModalFooter } from "@heroui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "../../../utils/toast";

import { ReportAbusePopup } from "../../../components/abuseReport/reportAbusePopup";
import {
  createContact,
  sendMessage,
  updateOpportunityCardStatus,
} from "../../../services/api";
import { MarketplaceCardResponseDto } from "../../../types/types";
import ConfirmModal from "../../../components/common/confirmModal";

const ACTION_BUTTON_CLASS = "h-9 min-w-0 rounded-xl px-4 text-sm font-medium";

interface RoleBasedActionsProps {
  viewCard: MarketplaceCardResponseDto;
  activeTab: "all" | "myCards";
  onClose: () => void;
  onRefresh?: () => void | Promise<void>;
}

interface LoginResponse {
  id?: string | number;
  profileType?: string;
}

interface RequestError {
  message?: string;
  response?: { data?: { message?: string } };
}

const getErrorMessage = (error: unknown, fallbackMessage: string): string => {
  const requestError = error as RequestError | undefined;
  return (
    requestError?.response?.data?.message ||
    requestError?.message ||
    fallbackMessage
  );
};

const isPastDate = (dateString?: string): boolean => {
  if (!dateString) {
    return false;
  }

  const eventDate = new Date(dateString);
  const today = new Date();

  eventDate.setHours(0, 0, 0, 0);
  today.setHours(0, 0, 0, 0);

  return eventDate < today;
};

export const RoleBasedActions = ({
  viewCard,
  activeTab,
  onClose,
  onRefresh,
}: RoleBasedActionsProps) => {
  const { t } = useTranslation("opportunities");

  const [confirmPopup, setConfirmPopup] = useState(false);
  const [confirmPublishPopup, setConfirmPublishPopup] = useState(false);
  const [showReportPopup, setShowReportPopup] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);

  let loginResponse: LoginResponse = {};

  try {
    loginResponse = JSON.parse(
      localStorage.getItem("loginResponse") || "{}"
    );
  } catch {
    loginResponse = {};
  }

  const loggedInUserId = loginResponse.id;
  const loggedInUserProfileType = loginResponse.profileType;

  const opportunity = viewCard.opportunityCardResponse;
  const eventDate = opportunity?.formData?.details?.eventDate;
  const opportunityStatus = opportunity?.status?.toUpperCase();

  const isExpired = isPastDate(eventDate);
  const isOpportunityAvailable = opportunityStatus === "AVAILABLE";
  const isPublished = opportunityStatus === "PUBLISH";
  const isStatusExpired = opportunityStatus === "EXPIRED";

  const handleCreateContact = async (
    cardId: string | number | undefined
  ) => {
    const manager = opportunity?.manager;
    const receiverId = manager?.id;

    if (!receiverId) {
      toast.error(t("missing_sender_or_receiver"));
      return;
    }

    if (cardId === undefined || cardId === null) {
      toast.error(
        t("invalid_opportunity_card")
      );
      return;
    }

    try {
      await createContact("opportunity", cardId);

      const receiverName =
        `${manager?.firstName || ""} ${manager?.lastName || ""}`.trim() ||
        "there";

      const sport = opportunity?.formData?.details?.sport || "this opportunity";
      const purse = opportunity?.formData?.core?.baseAmount;
      const currency = opportunity?.formData?.core?.currency;

      let purseText = "";

      if (purse !== undefined && purse !== null && currency) {
        purseText = ` with a purse of ${purse} ${currency}`;
      }

      try {
        await sendMessage({
          receiverId,
          message: `Hi ${receiverName}! I would like to contact you regarding your Opportunity for ${sport}${purseText}.`,
          chatType: "INDIVIDUAL",
          type: "CONTENT",
        });
      } catch (messageError) {
        console.warn(
          "Message failed but contact was created",
          messageError
        );
      }

      toast.success(t("contact_invite_success"));

      await onRefresh?.();
      onClose();
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          t("contact_invite_failed")
        )
      );
    }
  };

  const handlePublish = async (cardId: string) => {
    if (isPublishing) {
      return;
    }

    if (!isOpportunityAvailable) {
      toast.error(
        t("only_available_opportunity_cards_can_be_published")
      );
      return;
    }

    setIsPublishing(true);

    try {
      await updateOpportunityCardStatus(cardId);

      toast.success(t("card_published_successfully"));

      await onRefresh?.();
      onClose();
    } catch (error) {
      toast.error(
        getErrorMessage(
          error,
          t("failed_to_publish_card")
        )
      );
    } finally {
      setIsPublishing(false);
    }
  };

  let publishButtonColor: "danger" | "default" | "success" = "default";

  if (isExpired || isStatusExpired) {
    publishButtonColor = "danger";
  } else if (isOpportunityAvailable) {
    publishButtonColor = "success";
  }

  let publishButtonText = t("not_available");

  if (isExpired || isStatusExpired) {
    publishButtonText = t("expired");
  } else if (isPublished) {
    publishButtonText = t("published");
  } else if (isOpportunityAvailable && isPublishing) {
    publishButtonText = t("publishing");
  } else if (isOpportunityAvailable) {
    publishButtonText = t("publish");
  }

  const isOwnCard =
    String(loggedInUserId) === String(opportunity?.manager?.id || "");

  return (
    <ModalFooter className="flex-shrink-0 items-center justify-end gap-3 p-0">
      {!isOwnCard &&
        (opportunity?.reported ? (
          <Button
            variant="light"
            isDisabled
            className={`${ACTION_BUTTON_CLASS} text-gray-400`}
          >
            {t("reported")}
          </Button>
        ) : (
          <Button
            variant="light"
            color="danger"
            onPress={() => setShowReportPopup(true)}
            className={ACTION_BUTTON_CLASS}
          >
            {t("report_abuse")}
          </Button>
        ))}

      <Button
        variant="flat"
        onPress={onClose}
        className={`${ACTION_BUTTON_CLASS} bg-gray-100 text-gray-900 hover:bg-gray-200`}
      >
        {t("close")}
      </Button>

      {activeTab === "all" &&
        loggedInUserProfileType === "FIGHTER" &&
        (opportunity?.contacted || isExpired ? (
          <Button
            color={isExpired ? "danger" : "default"}
            isDisabled
            className={ACTION_BUTTON_CLASS}
          >
            {isExpired ? t("expired") : t("contacted")}
          </Button>
        ) : (
          <Button
            color="primary"
            className={`${ACTION_BUTTON_CLASS} bg-[#0080FF] text-white`}
            onPress={() => setConfirmPopup(true)}
          >
            {t("contact")}
          </Button>
        ))}

      {activeTab === "myCards" && (
        <Button
          color={publishButtonColor}
          isDisabled={!isOpportunityAvailable || isExpired || isPublishing}
          isLoading={isPublishing}
          className={ACTION_BUTTON_CLASS}
          onPress={() => {
            if (isOpportunityAvailable && !isExpired && viewCard.id) {
              setConfirmPublishPopup(true);
            }
          }}
        >
          {publishButtonText}
        </Button>
      )}

      <ConfirmModal
        isOpen={confirmPopup}
        onClose={() => setConfirmPopup(false)}
        cardTitle={t("confirm_contact")}
        cardDescription={t("confirm_contact_description")}
        buttonText={t("confirm")}
        onConfirm={() => handleCreateContact(viewCard.id)}
      />

      <ConfirmModal
        isOpen={confirmPublishPopup}
        onClose={() => setConfirmPublishPopup(false)}
        cardTitle={t("confirm_publish")}
        cardDescription={t("confirm_publish_description")}
        buttonText={t("confirm")}
        onConfirm={() => {
          if (viewCard.id) {
            handlePublish(viewCard.id);
          }
        }}
      />

      <ReportAbusePopup
        isOpen={showReportPopup}
        onClose={() => setShowReportPopup(false)}
        onSubmit={() => {
          setShowReportPopup(false);
          onClose();
        }}
        cardId={viewCard.id?.toString()}
        type="MARKETPLACECARDS"
        reportedTo={opportunity?.manager?.id ?? ""}
      />
    </ModalFooter>
  );
};
